import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle, ArrowLeft, ArrowRight, Check, FileText, LayoutTemplate, ListChecks, Loader2,
  MessageCircleQuestion, PenLine, Save, Send, ShieldCheck, Users,
} from 'lucide-react';
import {
  edgeFunctions, getCampaign, listCampaignHistory, listSegments, saveAssistentEntwurf, submitCampaignForApproval,
} from '../../lib/supabase';
import { vorabBefund, vorlage as findeVorlage, type Vorlage } from '../../lib/mailBuilder/vorlagen';
import { STAND_META, eingangAus, standVon } from '../../lib/freigabe';
import { editorPfad, istEinreicher, istRedaktion, istTeam, kannVorbereiten } from '../../lib/rollen';
import { formatDateTime } from '../../lib/format';
import { PreSendChecklist } from '../../components/PreSendChecklist';
import { offenePlatzhalter } from '../../lib/mailBuilder/platzhalter';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../../components/Toast';
import { Begleiter } from './Begleiter';
import { SchrittVorlage } from './SchrittVorlage';
import { SchrittFragen } from './SchrittFragen';
import { SchrittInhalt } from './SchrittInhalt';
import { SchrittEmpfaenger } from './SchrittEmpfaenger';
import { SchrittPruefen } from './SchrittPruefen';
import { Fertig, type Ergebnis } from './Fertig';
import {
  ABSENDER_EMAIL, ABSENDER_NAME, SCHRITTE, alsEntwurf, empfaengerFehler, fragenFehler, gruppenFuer, inhaltsFehler,
  kontaktOptionen, kontext, leererStand, mailHtml, ortText, standAusKampagne, standardKontakt, wunschZeitpunkt,
  type Stand, type VorlaufKontext,
} from './modell';
import './Assistent.css';
import './MailAssistent.css';

const KOPF = [
  { Icon: LayoutTemplate, titel: 'Worum geht es?', text: 'Wähl die Vorlage, die zu Deiner Mail passt.' },
  { Icon: ListChecks, titel: 'Was vorher stehen muss', text: 'Sportschule, Abhängigkeiten, Wunschtermin — damit nichts nachkorrigiert werden muss.' },
  { Icon: FileText, titel: 'Der Inhalt', text: 'Felder ausfüllen — die fertige Mail entsteht daneben.' },
  { Icon: Users, titel: 'Wer bekommt die Mail?', text: 'Eine oder mehrere Empfängergruppen wählen.' },
  { Icon: ShieldCheck, titel: 'Prüfen & Einreichen', text: 'Letzter Blick, Testmail, dann zur Redaktion.' },
];
const KOPF_SELBST = { Icon: ShieldCheck, titel: 'Prüfen & Versenden', text: 'Letzter Blick, Testmail, dann der Versand-Check.' };

/** Wert erst übernehmen, wenn eine Weile nichts mehr passiert (für die Vorschau). */
function useVerzoegert<T>(wert: T, ms: number): T {
  const [v, setV] = useState(wert);
  useEffect(() => {
    const t = setTimeout(() => setV(wert), ms);
    return () => clearTimeout(t);
  }, [wert, ms]);
  return v;
}

/** Betreff und Vorschautext nachführen, solange niemand sie selbst geschrieben hat. */
function mitVorschlaegen(s: Stand): Stand {
  const v = findeVorlage(s.vorlageId);
  if (!v) return s;
  const k = kontext(s);
  let n = s;
  if (!s.betreffManuell) n = { ...n, betreff: (v.betreff(s.daten, k)[0] ?? '').replace(/\s+/g, ' ').trim() };
  if (!s.vorschauManuell) n = { ...n, vorschautext: v.vorschautext(s.daten, k).trim() };
  return n;
}

function neuerStand(user: ReturnType<typeof useAuthStore.getState>['user'], v: Vorlage | null): Stand {
  const s = leererStand(user, v?.id ?? null);
  return v ? mitVorschlaegen({ ...s, daten: v.start(kontext(s)) }) : s;
}

export function MailAssistent() {
  const { id: routeId } = useParams<{ id?: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const user = useAuthStore((s) => s.user);

  const startVorlage = routeId ? null : findeVorlage(params.get('vorlage'));
  const [campaignId, setCampaignId] = useState<string | null>(routeId ?? null);
  const [stand, setStand] = useState<Stand>(() => neuerStand(user, startVorlage));
  const [schritt, setSchritt] = useState(startVorlage ? 1 : 0);
  const [erreicht, setErreicht] = useState(startVorlage ? 1 : 0);
  const [versucht, setVersucht] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gespeichert, setGespeichert] = useState<{ um: Date; json: string } | null>(null);
  const [geprueft, setGeprueft] = useState(false);
  const [testAn, setTestAn] = useState<string | null>(null);
  const [fertig, setFertig] = useState<Ergebnis | null>(null);
  const [checkOffen, setCheckOffen] = useState(false);
  const [versandArt, setVersandArt] = useState<'wunsch' | 'sofort'>('wunsch');
  const hydriertFuer = useRef<string | null>(null);

  const kampagne = useQuery({ queryKey: ['campaign', campaignId], queryFn: () => getCampaign(campaignId!), enabled: !!campaignId });
  // Nach einer Rückfrage läuft der Vorlauf ab dem Einreichen dieser Runde weiter.
  const rueckfrage = kampagne.data?.review_state === 'rueckfrage';
  const verlauf = useQuery({ queryKey: ['campaign-history', campaignId], queryFn: () => listCampaignHistory(campaignId!), enabled: !!campaignId && rueckfrage });
  const gruppenQ = useQuery({ queryKey: ['segments'], queryFn: listSegments });
  const gruppen = useMemo(() => gruppenQ.data ?? [], [gruppenQ.data]);
  const c = kampagne.data ?? null;

  // Bestehende Mail übernehmen — genau einmal je Mail, damit spätere
  // Cache-Updates keine laufenden Eingaben überschreiben.
  useEffect(() => {
    if (!c || hydriertFuer.current === c.id) return;
    hydriertFuer.current = c.id;
    const s = standAusKampagne(c);
    setStand(s);
    setGespeichert({ um: new Date(c.updated_at), json: JSON.stringify(s) });
    const bereit = !!findeVorlage(s.vorlageId);
    setSchritt(bereit ? 2 : 0);
    setErreicht(bereit ? SCHRITTE.length - 1 : 0);
  }, [c]);

  // Andere Adresse in derselben Seite: „Neue Mail" aus der Seitenleiste oder
  // eine andere Mail aus „Meine Mails".
  useEffect(() => {
    if ((routeId ?? null) === campaignId) return;
    hydriertFuer.current = null;
    setFertig(null);
    setCheckOffen(false);
    setVersucht(false);
    setFehler(null);
    setGeprueft(false);
    setTestAn(null);
    if (routeId) {
      setCampaignId(routeId);
    } else {
      setCampaignId(null);
      setGespeichert(null);
      setStand(neuerStand(user, null));
      setSchritt(0);
      setErreicht(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  const v = findeVorlage(stand.vorlageId);
  const vorschauStand = useVerzoegert(stand, 300);
  // Vorlage aus demselben (verzögerten) Stand nehmen — sonst trifft beim
  // Laden oder Wechseln eine neue Vorlage auf die Felder der alten.
  const vorschau = useMemo(() => {
    const vv = findeVorlage(vorschauStand.vorlageId);
    return vv ? mailHtml(vorschauStand, vv) : '';
  }, [vorschauStand]);
  const json = JSON.stringify(stand);
  const ungespeichert = !!v && erreicht >= 1 && json !== gespeichert?.json;

  useEffect(() => {
    if (!ungespeichert) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [ungespeichert]);

  const korrektur = !!c && !!user && c.created_by !== user.id && istRedaktion(user) && c.status === 'pending_approval';
  const einreicher = c ? !!c.requires_approval : istEinreicher(user);
  // Wer ohne Freigabe versenden darf (Admins, oder das Recht in „Benutzer & Rechte"),
  // verschickt eigene Mails nach dem Versand-Check selbst — alle anderen reichen ein.
  const selbst = !!user && user.canSendWithoutApproval && !korrektur && (!c || c.created_by === user.id);
  const vorlauf = useMemo<VorlaufKontext>(
    () => ({ selbst, eingang: (rueckfrage && eingangAus(verlauf.data ?? [])) || new Date() }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selbst, rueckfrage, verlauf.data, schritt],
  );

  // ── Änderungen ────────────────────────────────────────────────────────────
  const aendern = (teil: Partial<Stand>) => setStand((s) => mitVorschlaegen({ ...s, ...teil }));
  const setFeld = (id: string, wert: string) => setStand((s) => mitVorschlaegen({ ...s, daten: { ...s.daten, [id]: wert } }));
  const setAntwort = (id: string, wert: string) => setStand((s) => mitVorschlaegen({ ...s, antworten: { ...s.antworten, [id]: wert } }));

  const setSchulen = (neu: string[]) => setStand((s) => {
    let daten = s.daten;
    const vv = findeVorlage(s.vorlageId);
    if (vv) {
      // Aus der Sportschule vorbelegte Felder (z. B. der Ort) mitziehen, solange niemand sie geändert hat.
      const alt = vv.start(kontext(s));
      const jetzt = vv.start(kontext({ ...s, schulen: neu }));
      daten = { ...s.daten };
      for (const k of Object.keys(jetzt)) {
        if (alt[k] && s.daten[k] === alt[k] && jetzt[k] !== alt[k]) daten[k] = jetzt[k];
      }
    }
    const moeglich = kontaktOptionen({ schulen: neu }, user).map((o) => o.email);
    const eigeneWahl = s.kontaktEmail !== standardKontakt(s.schulen) && moeglich.includes(s.kontaktEmail);
    const passend = new Set(gruppenFuer({ ...s, schulen: neu }, user, gruppen).flatMap((a) => a.gruppen.map((g) => g.id)));
    return mitVorschlaegen({
      ...s,
      schulen: neu,
      daten,
      kontaktEmail: eigeneWahl ? s.kontaktEmail : standardKontakt(neu),
      segmentIds: s.segmentIds.filter((id) => passend.has(id)),
    });
  });

  const vorlageWaehlen = (nv: Vorlage) => {
    if (stand.vorlageId === nv.id) return;
    const alt = findeVorlage(stand.vorlageId);
    const hatInhalt = !!alt && Object.entries(stand.daten).some(([k, w]) => w?.trim() && alt.start(kontext(stand))[k] !== w);
    if (hatInhalt && !confirm(`Mit „${nv.name}" beginnen die Felder von vorn — Deine Eingaben aus „${alt!.name}" gehen verloren. Trotzdem wechseln?`)) return;
    setStand((s) => mitVorschlaegen({ ...s, vorlageId: nv.id, daten: nv.start(kontext(s)), antworten: {}, betreffManuell: false, vorschauManuell: false }));
  };

  // ── Prüfen je Schritt ───────────────────────────────────────────────────────
  const fehlerFuer = (i: number): string[] => {
    if (!v) return ['Wähl zuerst eine Vorlage.'];
    switch (SCHRITTE[i].id) {
      case 'vorlage': return [];
      case 'fragen': return fragenFehler(stand, v, vorlauf);
      case 'inhalt': return inhaltsFehler(stand, v);
      case 'empfaenger': return empfaengerFehler(stand, gruppen);
      case 'pruefen': return pruefFehler(vorlauf);
    }
  };
  /** Alles, was vor dem Einreichen oder Versenden stehen muss — offene Punkte halten jede Mail auf. */
  const pruefFehler = (vk: VorlaufKontext): string[] => {
    if (!v) return ['Wähl zuerst eine Vorlage.'];
    const alle = [...fragenFehler(stand, v, vk), ...inhaltsFehler(stand, v), ...empfaengerFehler(stand, gruppen)];
    const offen = vorabBefund(v, stand.antworten).klaeren.map((k) => `Noch offen: ${k}`);
    return [...alle, ...offen, ...(geprueft ? [] : ['Setz den Haken bei „Ich habe die Mail geprüft".'])];
  };
  const aktuelleFehler = versucht ? fehlerFuer(schritt) : [];

  const nachOben = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // ── Speichern, Test, Einreichen ─────────────────────────────────────────────
  const speichern = async (leise = false): Promise<string | null> => {
    if (!v || !user) return null;
    setSpeichert(true);
    setFehler(null);
    try {
      const gespeicherterStand = stand;
      const neu = await saveAssistentEntwurf(campaignId, alsEntwurf(gespeicherterStand, v));
      hydriertFuer.current = neu.id;
      qc.setQueryData(['campaign', neu.id], neu);
      void qc.invalidateQueries({ queryKey: ['my-campaigns'] });
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
      setGespeichert({ um: new Date(), json: JSON.stringify(gespeicherterStand) });
      if (!campaignId) {
        setCampaignId(neu.id);
        navigate(`/neue-mail/${neu.id}`, { replace: true });
      }
      if (!leise) toast.success('Entwurf gespeichert.');
      return neu.id;
    } catch (e) {
      setFehler(`Speichern hat nicht geklappt: ${(e as Error).message}`);
      return null;
    } finally {
      setSpeichert(false);
    }
  };

  const springen = (i: number) => {
    if (i > erreicht || i === schritt) return;
    if (ungespeichert) void speichern(true);
    setSchritt(i);
    setVersucht(false);
    nachOben();
  };

  const weiter = async () => {
    const f = fehlerFuer(schritt);
    if (f.length) { setVersucht(true); return; }
    if (schritt >= 1) {
      const id = await speichern(true);
      if (!id) return;
    }
    const n = Math.min(schritt + 1, SCHRITTE.length - 1);
    setSchritt(n);
    setErreicht((e) => Math.max(e, n));
    setVersucht(false);
    nachOben();
  };

  const zurueck = () => {
    if (schritt === 0) return;
    if (ungespeichert) void speichern(true);
    setSchritt(schritt - 1);
    setVersucht(false);
    nachOben();
  };

  const testmail = async (adresse: string) => {
    const id = await speichern(true);
    if (!id) return;
    await edgeFunctions.sendTestEmail(id, [adresse]);
    setTestAn(adresse);
  };

  const einreichen = async () => {
    // Auch wer selbst versenden darf, kann zur Freigabe geben — dann gilt der Vorlauf der Redaktion.
    const f = pruefFehler({ ...vorlauf, selbst: false });
    if (f.length) { setVersucht(true); setFehler(selbst ? f.join(' ') : null); return; }
    const id = await speichern(true);
    if (!id) return;
    setSendet(true);
    setFehler(null);
    try {
      await submitCampaignForApproval(id, stand.nachricht);
      void qc.invalidateQueries({ queryKey: ['my-campaigns'] });
      void qc.invalidateQueries({ queryKey: ['campaign', id] });
      setFertig({ art: 'eingereicht' });
      nachOben();
    } catch (e) {
      setFehler(`Einreichen hat nicht geklappt: ${(e as Error).message}`);
    } finally {
      setSendet(false);
    }
  };

  const korrekturSpeichern = async () => {
    const id = await speichern(true);
    if (id) {
      toast.success('Korrektur gespeichert.');
      navigate(`/freigaben?mail=${id}`);
    }
  };

  const wunsch = wunschZeitpunkt(stand);
  // Einplanen nur, wenn der Termin noch mindestens zehn Minuten entfernt ist.
  const planbar = !!wunsch && wunsch.getTime() > Date.now() + 10 * 60_000;
  const planen = versandArt === 'wunsch' && planbar;

  /** Versand-Check öffnen — erst wenn alles andere stimmt. */
  const zumCheck = () => {
    const f = fehlerFuer(schritt);
    if (f.length) { setVersucht(true); return; }
    setCheckOffen(true);
  };

  const versenden = async () => {
    const id = await speichern(true);
    if (!id) { setCheckOffen(false); return; }
    setSendet(true);
    setFehler(null);
    try {
      await edgeFunctions.sendCampaign(id, planen ? wunsch!.toISOString() : undefined);
      void qc.invalidateQueries({ queryKey: ['my-campaigns'] });
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
      void qc.invalidateQueries({ queryKey: ['campaign', id] });
      setCheckOffen(false);
      setFertig(planen ? { art: 'geplant', wann: wunsch!.toISOString() } : { art: 'versendet' });
      nachOben();
    } catch (e) {
      setCheckOffen(false);
      setFehler(`Versand hat nicht geklappt: ${(e as Error).message}`);
    } finally {
      setSendet(false);
    }
  };

  // ── Sonderfälle ───────────────────────────────────────────────────────────
  if (!kannVorbereiten(user)) {
    return (
      <div className="assistent"><div className="assistent-karte">
        <h2 className="as-sonder-titel">Mails vorbereiten ist für Deine Rolle nicht freigeschaltet</h2>
        <p>Mit „Nur lesen" kannst Du Kampagnen und Vorlagen ansehen, aber keine Mails vorbereiten. Wende Dich an die Redaktion, wenn Du das brauchst.</p>
      </div></div>
    );
  }
  if (campaignId && kampagne.isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><div className="loading-spinner" /></div>;
  }
  if (campaignId && (kampagne.isError || (!kampagne.isLoading && !c))) {
    return (
      <div className="assistent"><div className="assistent-karte">
        <h2 className="as-sonder-titel">Diese Mail gibt es nicht (mehr)</h2>
        <p>Vielleicht wurde sie gelöscht — oder sie gehört jemand anderem.</p>
        <Link to="/meine-mails" className="btn btn-primary">Zu meinen Mails</Link>
      </div></div>
    );
  }
  if (c && !c.builder_template) {
    if (!istTeam(user)) {
      return (
        <div className="assistent"><div className="assistent-karte">
          <h2 className="as-sonder-titel">Diese Mail wurde nicht im Assistenten gebaut</h2>
          <p>Sie kommt zum Beispiel aus dem Event Manager. Unter „Geplante Mails“ siehst Du sie und schlägst Änderungen direkt im Text vor; die Redaktion übernimmt sie.</p>
          <Link to={editorPfad(user, c.id)} className="btn btn-primary">Ansehen und Änderungen vorschlagen</Link>
        </div></div>
      );
    }
    return (
      <div className="assistent"><div className="assistent-karte">
        <h2 className="as-sonder-titel">Diese Kampagne kommt aus dem Kampagnen-Editor</h2>
        <p>Sie wurde nicht mit dem Assistenten gebaut und lässt sich deshalb nur im Kampagnen-Editor bearbeiten.</p>
        <Link to={editorPfad(user, c.id)} className="btn btn-primary">Im Kampagnen-Editor öffnen</Link>
      </div></div>
    );
  }
  if (c && !fertig && !korrektur && c.status !== 'draft') {
    const meta = STAND_META[standVon(c)];
    return (
      <div className="assistent"><div className="assistent-karte">
        <h2 className="as-sonder-titel">Diese Mail lässt sich gerade nicht ändern</h2>
        <p>Stand: <span className={`badge ${meta.badge}`}>{meta.label}</span> — {meta.text}</p>
        <p>Eingereichte Mails ändert nur die Redaktion. Solange die Mail noch nicht freigegeben ist, kannst Du sie unter „Meine Mails" zurückziehen und dann ändern.</p>
        <Link to={`/meine-mails?mail=${c.id}`} className="btn btn-primary">Zur Mail</Link>
      </div></div>
    );
  }

  if (fertig) {
    return (
      <div className="assistent">
        <Fertig stand={stand} ergebnis={fertig} onNochEine={() => navigate('/neue-mail')} />
      </div>
    );
  }

  const kopf = selbst && SCHRITTE[schritt].id === 'pruefen' ? KOPF_SELBST : KOPF[schritt];
  const breit = SCHRITTE[schritt].id === 'inhalt';

  return (
    <div className={`assistent${breit ? ' assistent--breit' : ''}`}>
      <div className="assistent-kopf as-kopf">
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1>{korrektur ? 'Mail korrigieren' : campaignId ? 'Mail bearbeiten' : 'Neue Mail'}</h1>
          <p>
            {v ? <>{v.emoji} {v.name}{ortText(stand) ? ` · ${ortText(stand)}` : ''}</> : 'Schritt für Schritt zur fertigen Mail — im Randori-Layout, ohne HTML.'}
          </p>
        </div>
        {v && erreicht >= 1 && (
          <div className="as-speicherstand" data-tour="assistent-status" aria-live="polite">
            {speichert ? <><Loader2 size={14} className="spin" aria-hidden="true" /> Speichert …</>
              : ungespeichert ? 'Noch nicht gespeichert'
              : gespeichert ? <><Check size={14} aria-hidden="true" /> Gespeichert {gespeichert.um.toDateString() === new Date().toDateString()
                ? gespeichert.um.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
                : `am ${formatDateTime(gespeichert.um.toISOString())}`}</> : null}
          </div>
        )}
      </div>

      {korrektur && c && (
        <div className="alert alert-info">
          <PenLine size={16} aria-hidden="true" />
          <div>
            <strong>Korrektur durch die Redaktion.</strong> Du änderst die Mail von {c.created_by_name ?? 'der einreichenden Person'} vor der Freigabe.
            Kleinigkeiten gern direkt — bei inhaltlichen Fragen lieber eine Rückfrage stellen.
          </div>
        </div>
      )}

      {c?.review_state === 'rueckfrage' && c.rejection_reason && (
        <div className="as-rueckfrage" role="status">
          <MessageCircleQuestion size={18} aria-hidden="true" />
          <div>
            <strong>Rückfrage der Redaktion{c.rejected_at ? ` vom ${formatDateTime(c.rejected_at)}` : ''}:</strong>
            <p>{c.rejection_reason}</p>
            <span>Pass die Mail an und reich sie im letzten Schritt erneut ein.</span>
          </div>
        </div>
      )}

      <ol className="assistent-fortschritt" data-tour="assistent-schritte" aria-label="Schritte">
        {SCHRITTE.map((s, i) => {
          const aktiv = i === schritt;
          const erledigt = !aktiv && i < erreicht;
          return (
            <li key={s.id}>
              <button
                type="button"
                className={`fortschritt-punkt${aktiv ? ' aktiv' : erledigt ? ' erledigt' : ''}`}
                onClick={() => springen(i)}
                disabled={i > erreicht}
                aria-current={aktiv ? 'step' : undefined}
              >
                <span className="fortschritt-nummer">{erledigt ? <Check size={12} aria-hidden="true" /> : i + 1}</span>
                <span className="fortschritt-label">{s.label}</span>
              </button>
            </li>
          );
        })}
      </ol>

      <Begleiter schritt={SCHRITTE[schritt].id} selbst={selbst} />

      <div className="assistent-karte">
        <div className="assistent-frage">
          <span className="assistent-frage-icon" aria-hidden="true"><kopf.Icon size={22} /></span>
          <div>
            <h2>{kopf.titel}</h2>
            <p>{kopf.text}</p>
          </div>
        </div>

        <div className="assistent-inhalt">
          {schritt === 0 && (
            <div data-tour="assistent-arbeit"><SchrittVorlage gewaehlt={stand.vorlageId} onWahl={vorlageWaehlen} /></div>
          )}
          {schritt === 1 && v && (
            <div data-tour="assistent-arbeit">
              <SchrittFragen v={v} stand={stand} user={user} setSchulen={setSchulen} setAntwort={setAntwort} aendern={aendern} zeigeFehler={versucht} vorlauf={vorlauf} />
            </div>
          )}
          {schritt === 2 && v && (
            <SchrittInhalt v={v} stand={stand} html={vorschau} setFeld={setFeld} aendern={aendern} zeigeFehler={versucht} />
          )}
          {schritt === 3 && v && (
            <SchrittEmpfaenger stand={stand} user={user} gruppen={gruppen} laedt={gruppenQ.isLoading} aendern={aendern} zeigeFehler={versucht} />
          )}
          {schritt === 4 && v && (
            <SchrittPruefen
              stand={stand} v={v} html={mailHtml(stand, v)} user={user} gruppen={gruppen}
              aendern={aendern} springen={springen} geprueft={geprueft} setGeprueft={setGeprueft}
              testmail={testmail} testGesendetAn={testAn} einreicher={einreicher && !korrektur} zeigeFehler={versucht}
              selbst={selbst} vorlauf={vorlauf} versandArt={planbar ? versandArt : 'sofort'} setVersandArt={setVersandArt} planbar={planbar}
            />
          )}
        </div>

        {aktuelleFehler.length > 0 && (
          <div className="alert alert-danger assistent-fehler" role="alert">
            <AlertCircle size={16} aria-hidden="true" />
            <div>
              <strong>Bevor es weitergeht:</strong>
              <ul className="as-fehlerliste">{aktuelleFehler.map((f) => <li key={f}>{f}</li>)}</ul>
            </div>
          </div>
        )}
        {fehler && (
          <div className="alert alert-danger assistent-fehler" role="alert"><AlertCircle size={16} aria-hidden="true" /><div>{fehler}</div></div>
        )}

        <div className="assistent-fuss">
          <button type="button" className="btn btn-ghost" onClick={zurueck} disabled={schritt === 0}>
            <ArrowLeft size={16} aria-hidden="true" /> Zurück
          </button>
          <span style={{ flex: 1 }} />
          {v && schritt >= 1 && (
            <button type="button" className="btn btn-secondary" onClick={() => void speichern()} disabled={speichert} data-tour="assistent-speichern">
              <Save size={16} aria-hidden="true" /> Entwurf speichern
            </button>
          )}
          {schritt < SCHRITTE.length - 1 ? (
            <button type="button" className="btn btn-primary btn-gross" onClick={weiter} disabled={speichert} data-tour="assistent-weiter">
              Weiter <ArrowRight size={16} aria-hidden="true" />
            </button>
          ) : korrektur ? (
            <button type="button" className="btn btn-primary btn-gross" onClick={korrekturSpeichern} disabled={speichert} data-tour="assistent-weiter">
              <Save size={16} aria-hidden="true" /> Korrektur speichern
            </button>
          ) : selbst ? (
            <>
              <button type="button" className="btn btn-ghost" onClick={einreichen} disabled={speichert || sendet}
                title="Die Redaktion prüft die Mail vor dem Versand — wie bei allen, die ohne Freigabe nicht versenden dürfen.">
                Stattdessen zur Freigabe geben
              </button>
              <button type="button" className="btn btn-primary btn-gross" onClick={zumCheck} disabled={speichert || sendet} data-tour="assistent-weiter">
                <ShieldCheck size={16} aria-hidden="true" /> Letzter Check &amp; {planen ? 'einplanen' : 'versenden'}
              </button>
            </>
          ) : (
            <button type="button" className="btn btn-primary btn-gross" onClick={einreichen} disabled={speichert || sendet} data-tour="assistent-weiter">
              {sendet ? <Loader2 size={16} className="spin" aria-hidden="true" /> : <Send size={16} aria-hidden="true" />}
              {sendet ? 'Wird eingereicht …' : c?.review_state === 'rueckfrage' ? 'Erneut einreichen' : 'Zur Freigabe einreichen'}
            </button>
          )}
        </div>
      </div>

      {checkOffen && v && (
        <PreSendChecklist
          isOpen
          onClose={() => setCheckOffen(false)}
          onConfirm={versenden}
          pending={sendet}
          mode="send"
          ctaLabel={planen ? `Für ${formatDateTime(wunsch!.toISOString())} einplanen` : 'Jetzt versenden'}
          meta={{
            name: v.name,
            subject: stand.betreff,
            senderName: ABSENDER_NAME,
            senderEmail: ABSENDER_EMAIL,
            recipientGroupCount: stand.segmentIds.length,
            manualRecipientCount: 0,
            hasUnsubscribe: true,
            hasBrowserView: false,
            designMode: 'template',
            hasSentTestEmail: !!testAn,
            placeholders: offenePlatzhalter(vorschau, stand.betreff, stand.vorschautext),
          }}
        />
      )}
    </div>
  );
}
